import {
  Captions,
  Copy,
  Download,
  FileText,
  History,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Square,
  Trash2,
} from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { Button } from '@/components/ui/Button'
import {
  DialogClose,
  DialogContent,
  DialogRoot,
} from '@/components/ui/Dialog'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { IconButton } from '@/components/ui/IconButton'
import { Input } from '@/components/ui/Input'
import {
  useDeleteVideo,
  useDuplicateVideo,
  useUpdateVideoMetadata,
  type VideoItem,
} from '@/api/endpoints/videos'
import { errorMessage } from '@/lib/errors'
import { preservingChannel } from '@/lib/routing'
import styles from './ProjectActions.module.scss'
import { projectsLinkSearch } from '@/features/projects/filters'

export interface ProjectActionsProps {
  video: VideoItem
  mp4Url: string | null
}

type OpenDialog = 'none' | 'rename' | 'duplicate' | 'delete'

/**
 * Context menu + action dialogs for a Project Detail page. Owns the "which
 * dialog is open" state so the caller just drops this in the header's
 * `moreSlot` and forgets about the machinery.
 */
export function ProjectActions({ video, mp4Url }: ProjectActionsProps) {
  const [open, setOpen] = useState<OpenDialog>('none')
  const close = () => {
    setOpen('none')
  }

  const canDownload = Boolean(mp4Url) && video.ui_status !== 'gerando'
  const isGenerating = video.ui_status === 'gerando'

  return (
    <>
      <DropdownMenuRoot>
        <DropdownMenuTrigger asChild>
          <IconButton aria-label="Mais ações" variant="outline">
            <MoreHorizontal />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Projeto</DropdownMenuLabel>
          <DropdownMenuItem
            leadingIcon={<Pencil />}
            hint="⌘E"
            onSelect={() => {
              setOpen('rename')
            }}
          >
            Renomear
          </DropdownMenuItem>
          <DropdownMenuItem
            leadingIcon={<Copy />}
            onSelect={() => {
              setOpen('duplicate')
            }}
          >
            Duplicar
          </DropdownMenuItem>
          <DropdownMenuItem leadingIcon={<History />} disabled>
            Ver histórico de versões
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel>Geração</DropdownMenuLabel>
          <DropdownMenuItem leadingIcon={<RefreshCw />} disabled>
            Regerar tudo
          </DropdownMenuItem>
          <DropdownMenuItem leadingIcon={<Square />} disabled={!isGenerating}>
            Cancelar geração atual
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel>Exportar</DropdownMenuLabel>
          <DropdownMenuItem
            leadingIcon={<Download />}
            disabled={!canDownload}
            onSelect={() => {
              if (mp4Url) window.open(mp4Url, '_blank', 'noopener')
            }}
          >
            Baixar MP4
          </DropdownMenuItem>
          <DropdownMenuItem leadingIcon={<FileText />} disabled>
            Exportar roteiro
          </DropdownMenuItem>
          <DropdownMenuItem leadingIcon={<Captions />} disabled>
            Exportar legendas (SRT)
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuItem
            leadingIcon={<Trash2 />}
            danger
            onSelect={() => {
              setOpen('delete')
            }}
          >
            Arquivar projeto
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuRoot>

      <RenameDialog
        open={open === 'rename'}
        onClose={close}
        videoId={video.id}
        lang={video.language}
        currentTitle={video.meta_title ?? video.metadata?.title ?? ''}
      />
      <DuplicateDialog
        open={open === 'duplicate'}
        onClose={close}
        videoId={video.id}
        lang={video.language}
      />
      <DeleteDialog
        open={open === 'delete'}
        onClose={close}
        videoId={video.id}
        lang={video.language}
        channel={video.channel}
        title={video.metadata?.title ?? video.meta_title ?? video.id}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Rename
// ---------------------------------------------------------------------------

interface RenameDialogProps {
  open: boolean
  onClose: () => void
  videoId: string
  lang: string
  currentTitle: string
}

function RenameDialog({ open, onClose, videoId, lang, currentTitle }: RenameDialogProps) {
  const [title, setTitle] = useState(currentTitle)
  const mutation = useUpdateVideoMetadata()

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    mutation.mutate(
      { videoId, lang, meta_title: title.trim() },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title="Renomear projeto"
        description="Atualiza o título usado ao publicar no YouTube (meta_title). O título gerado pelo pipeline permanece como fallback."
        footer={
          <>
            <DialogClose asChild>
              <Button variant="secondary" disabled={mutation.isPending}>
                Cancelar
              </Button>
            </DialogClose>
            <Button
              onClick={onSubmit}
              disabled={mutation.isPending || title.trim() === currentTitle.trim()}
            >
              {mutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </>
        }
      >
        <form onSubmit={onSubmit}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="rename-title">
              Título
            </label>
            <Input
              id="rename-title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value)
              }}
              disabled={mutation.isPending}
              error={mutation.isError}
            />
          </div>
          {mutation.isError ? (
            <p className={styles.error}>{errorMessage(mutation.error)}</p>
          ) : null}
        </form>
      </DialogContent>
    </DialogRoot>
  )
}

// ---------------------------------------------------------------------------
// Duplicate
// ---------------------------------------------------------------------------

interface DuplicateDialogProps {
  open: boolean
  onClose: () => void
  videoId: string
  lang: string
}

function DuplicateDialog({ open, onClose, videoId, lang }: DuplicateDialogProps) {
  const navigate = useNavigate()
  const [newId, setNewId] = useState(() => suggestDuplicateId(videoId))
  const mutation = useDuplicateVideo()

  const trimmed = newId.trim().toLowerCase()
  const isValid = /^[a-z0-9][a-z0-9-]*$/.test(trimmed)

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid) return
    mutation.mutate(
      { videoId, lang, newId: trimmed },
      {
        onSuccess: (data) => {
          onClose()
          void navigate({
            to: '/projects/$projectId',
            params: { projectId: data.id },
            search: preservingChannel({ lang }),
          })
        },
      },
    )
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose()
          setNewId(suggestDuplicateId(videoId))
        }
      }}
    >
      <DialogContent
        title="Duplicar projeto"
        description="Cria um novo projeto com o mesmo MP4 em disco. Use um id curto, só letras minúsculas, números e hífens."
        footer={
          <>
            <DialogClose asChild>
              <Button variant="secondary" disabled={mutation.isPending}>
                Cancelar
              </Button>
            </DialogClose>
            <Button onClick={onSubmit} disabled={!isValid || mutation.isPending}>
              {mutation.isPending ? 'Duplicando...' : 'Duplicar'}
            </Button>
          </>
        }
      >
        <form onSubmit={onSubmit}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="duplicate-id">
              Novo id
            </label>
            <Input
              id="duplicate-id"
              value={newId}
              onChange={(e) => {
                setNewId(e.target.value)
              }}
              disabled={mutation.isPending}
              error={mutation.isError || (newId.length > 0 && !isValid)}
            />
            {!isValid && newId.length > 0 ? (
              <span className={styles.hint}>
                Use apenas letras minúsculas, números e hífens. Ex: {videoId}-copia
              </span>
            ) : null}
          </div>
          {mutation.isError ? (
            <p className={styles.error}>{errorMessage(mutation.error)}</p>
          ) : null}
        </form>
      </DialogContent>
    </DialogRoot>
  )
}

function suggestDuplicateId(videoId: string): string {
  // Append "-copia" when absent; otherwise bump the counter to keep the
  // suggestion unique-looking across retries in the same session.
  const base = videoId.replace(/-copia(-\d+)?$/, '')
  return `${base}-copia`
}

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

interface DeleteDialogProps {
  open: boolean
  onClose: () => void
  videoId: string
  lang: string
  channel: string
  title: string
}

function DeleteDialog({ open, onClose, videoId, lang, channel, title }: DeleteDialogProps) {
  const navigate = useNavigate()
  const mutation = useDeleteVideo()
  // Volta ao canal de onde a pessoa veio (o ?channel= do detalhe). O canal do
  // vídeo pode ser um pool, que não é canal da lista e cairia no primeiro.
  const search = useSearch({ strict: false })
  const backTo = 'channel' in search && typeof search.channel === 'string' && search.channel ? search.channel : channel

  function confirm() {
    mutation.mutate(
      { videoId, lang, channel },
      {
        onSuccess: () => {
          onClose()
          void navigate({ to: '/', search: projectsLinkSearch(backTo) })
        },
      },
    )
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title="Arquivar projeto?"
        description="O MP4, miniaturas e project.json deste vídeo serão removidos do disco. A ação não pode ser desfeita."
        footer={
          <>
            <DialogClose asChild>
              <Button variant="secondary" disabled={mutation.isPending}>
                Cancelar
              </Button>
            </DialogClose>
            <Button onClick={confirm} disabled={mutation.isPending}>
              {mutation.isPending ? 'Arquivando...' : 'Arquivar'}
            </Button>
          </>
        }
      >
        <p className={styles.body}>
          <span className={styles.bodyLabel}>Projeto:</span>{' '}
          <span className={styles.bodyValue}>{title}</span>
        </p>
        {mutation.isError ? (
          <p className={styles.error}>{errorMessage(mutation.error)}</p>
        ) : null}
      </DialogContent>
    </DialogRoot>
  )
}
